import type { RefAttributes, SVGProps } from 'react'

import {
  BedDouble,
  Building2Icon,
  BuildingIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsUpDownIcon,
  CircleIcon,
  DotIcon,
  EllipsisIcon,
  Eye,
  EyeOff,
  HandshakeIcon,
  Loader2,
  LogOutIcon,
  Maximize2,
  MenuIcon,
  ScrollTextIcon,
  TriangleAlert,
  XIcon,
  FlameIcon,
  Timer,
  User,
  Undo2Icon,
  Blocks,
  RefreshCcw,
  RefreshCw,
  Info,
  CircleCheck,
  CircleAlert,
  CircleX,
  KeyRoundIcon,
  MoveDiagonalIcon,
  ClockIcon,
  BanknoteIcon,
  CalendarRangeIcon,
  MinusIcon,
  PlusIcon,
  ImportIcon,
  ChevronDownIcon,
  Trash2Icon,
  SquareMousePointer,
  Search,
  ChevronFirstIcon,
  ChevronLastIcon,
  ChevronUp,
  ArrowLeft,
  Pencil,
  SquarePlay,
  CircleHelp,
  CircleCheckBig,
  SendIcon,
  Upload,
  CopyIcon,
  FilesIcon,
  DownloadIcon,
  CameraIcon,
  HotelIcon,
  BookOpenIcon,
  LandPlot,
  SettingsIcon,
  FileArchiveIcon,
} from 'lucide-react'

import { FloorPlanIcon } from '@/shared/components/icon/floor-plan'
import { cn } from '@/shared/lib'

export const icones = {
  send: SendIcon,
  minus: MinusIcon,
  plus: PlusIcon,
  'floor-plan': FloorPlanIcon,
  logout: LogOutIcon,
  close: XIcon,
  menu: MenuIcon,
  camera: CameraIcon,
  check: CheckIcon,
  pencil: Pencil,
  'chevron-left': ChevronLeftIcon,
  'chevron-right': ChevronRightIcon,
  circle: CircleIcon,
  ellipsis: EllipsisIcon,
  building: BuildingIcon,
  'chevron-up-down': ChevronsUpDownIcon,
  dot: DotIcon,
  'eye-open': Eye,
  'eye-off': EyeOff,
  loader: Loader2,
  'triangle-alert': TriangleAlert,
  'building-2': Building2Icon,
  handshake: HandshakeIcon,
  hotel: HotelIcon,
  'scroll-text': ScrollTextIcon,
  'bed-double': BedDouble,
  'maximize-2': Maximize2,
  flame: FlameIcon,
  timer: Timer,
  user: User,
  'undo-2': Undo2Icon,
  blocks: Blocks,
  'book-open': BookOpenIcon,
  'refresh-ccw': RefreshCcw,
  'refresh-cw': RefreshCw,
  info: Info,
  'circle-alert': CircleAlert,
  'circle-check': CircleCheck,
  'circle-close': CircleX,
  'key-round': KeyRoundIcon,
  'move-diagonal': MoveDiagonalIcon,
  clock: ClockIcon,
  banknote: BanknoteIcon,
  'calendar-range': CalendarRangeIcon,
  import: ImportIcon,
  'chevron-down': ChevronDownIcon,
  'chevron-up': ChevronUp,
  'trash-2': Trash2Icon,
  'square-mouse-pointer': SquareMousePointer,
  search: Search,
  'chevron-first': ChevronFirstIcon,
  'chevron-last': ChevronLastIcon,
  'arrow-left': ArrowLeft,
  'circle-help': CircleHelp,
  'square-play': SquarePlay,
  'circle-check-big': CircleCheckBig,
  'land-plot': LandPlot,
  upload: Upload,
  copy: CopyIcon,
  files: FilesIcon,
  download: DownloadIcon,
  settings: SettingsIcon,
  'file-archive': FileArchiveIcon,
} as const

type TIcon = keyof typeof icones
type SVGAttributes = Partial<SVGProps<SVGSVGElement>>
type ElementAttributes = RefAttributes<SVGSVGElement> & SVGAttributes
interface IIconProps extends ElementAttributes {
  name: TIcon
  size?: string | number
  absoluteStrokeWidth?: boolean
}
export const Icon = ({ name, size, className, ...props }: IIconProps) => {
  const Comp = icones[name]

  const styleWithSize = size
    ? {
        '--icon-size': typeof size === 'number' ? `${size}px` : size,
        width: typeof size === 'number' ? `${size}px` : size,
        height: typeof size === 'number' ? `${size}px` : size,
        ...props.style,
      }
    : props.style

  return <Comp {...props} style={styleWithSize} className={cn('custom-icon', className)} data-testid={`icon-${name}`} />
}
